import { Popover } from "radix-ui";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/foundation/components/Button";
import { workErrorMessage } from "../workTypes";

// Bench (work-bench 01 §2) — a decision lives where it is shown: the question in plain words, at most three answers,
// one line of why; an answer that needs words shows its field INSIDE the popover. Never a modal, never a confirm of a
// confirm; a failed action keeps the popover open with the server's sentence.

export interface DecisionAnswer {
  id: string;
  label: string;
  variant?: "primary" | "neutral" | "danger";
  /** The answer needs words (a note, a reason): a field appears inside the popover, the button waits for text. */
  needsText?: boolean;
  acceptsText?: boolean;
  description?: string;
  placeholder?: string;
}

export interface DecisionPopoverProps {
  /** The button (or any control) that raises the decision. */
  children?: ReactNode;
  /** Blocking choices remain visible without opening a menu. */
  inline?: boolean;
  question: string;
  because?: string | null;
  answers: DecisionAnswer[];
  onPick: (answerId: string, text?: string) => Promise<void> | void;
  /** Said once the pick succeeded (the popover closes itself). */
  onDone?: (answerId: string) => void;
  side?: "top" | "bottom" | "left" | "right";
  disabled?: boolean;
}

export function DecisionPopover({
  children,
  inline = false,
  question,
  because,
  answers,
  onPick,
  onDone,
  side = "bottom",
  disabled,
}: DecisionPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = inline ? answers : answers.slice(0, 3);
  const needsText = shown.some((a) => a.needsText || a.acceptsText);

  const pick = async (a: DecisionAnswer) => {
    if (a.needsText && !text.trim()) return;
    setBusy(a.id);
    setError(null);
    try {
      await onPick(a.id, a.needsText || a.acceptsText ? text.trim() : undefined);
      setOpen(false);
      setText("");
      if (inline) setDone(a.label);
      onDone?.(a.id);
    } catch (e) {
      setError(workErrorMessage(e, t("That did not work.")));
    } finally {
      setBusy(null);
    }
  };

  const content = (
    <>
      <p className="font-medium text-sm">{question}</p>
      {because ? (
        <p className="mt-1 text-[color:var(--text-secondary)] text-xs">{because}</p>
      ) : null}
      {needsText ? (
        <label className="mt-2 block text-xs text-text-secondary">
          {inline
            ? shown.some((a) => a.needsText)
              ? t("Add the requested note to enable that choice.")
              : t("Optional note")
            : null}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={shown.find((a) => a.needsText)?.placeholder ?? t("A few words…")}
            aria-label={t("Your words")}
            rows={inline ? 2 : 3}
            className="mt-2 w-full rounded-md border border-input-border bg-bg p-2 text-sm text-text"
          />
        </label>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {shown.map((a) => (
          <div key={a.id} className={inline ? "flex min-w-0 flex-col gap-1" : undefined}>
            <Button
              size="sm"
              className={inline ? "h-auto min-h-9 whitespace-normal py-2 text-start" : undefined}
              variant={a.variant ?? "neutral"}
              disabled={disabled || busy !== null || done !== null || (a.needsText && !text.trim())}
              onClick={() => void pick(a)}
            >
              {busy === a.id ? t("Working…") : a.label}
            </Button>
            {inline && a.description ? (
              <p className="max-w-72 text-xs text-text-secondary">{a.description}</p>
            ) : null}
          </div>
        ))}
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-[color:var(--danger)] text-xs">
          {error}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="mt-2 text-xs text-text-secondary">
          {t("Applied: {{choice}}", { choice: done })}
        </p>
      ) : null}
    </>
  );
  if (inline)
    return (
      <section
        aria-label={question}
        className="w-full min-w-0 rounded-lg border border-divider bg-bg p-3"
      >
        {content}
      </section>
    );
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <Popover.Trigger asChild disabled={disabled}>
        {children}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="bn-popover z-50 w-[min(360px,calc(100vw-24px))] rounded-md border border-[color:var(--divider)] bg-[color:var(--bg)] p-3 text-[color:var(--text)] shadow-lg"
          role="dialog"
          aria-label={question}
        >
          {content}
          <Popover.Arrow className="fill-[color:var(--divider)]" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
